import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { internshipApi, matchingApi } from '@/api';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  ErrorState,
  Icon,
  LoadingState,
} from '@/components/ui';
import { Unavailable } from '@/components/marketing/Unavailable';
import { ApplyDialog } from '@/features/applications/ApplyDialog';
import { CompanyLogo } from '@/features/internships/CompanyLogo';
import { ListingItems, ListingProse } from '@/features/internships/ListingText';
import { ShareButton } from '@/features/internships/ShareButton';
import { useSaveInternship } from '@/features/internships/useSaveInternship';
import { MatchBadge } from '@/features/matching/MatchBadge';
import { MatchBreakdown } from '@/features/matching/MatchBreakdown';
import { ResumeMatchCard } from '@/features/matching/ResumeMatchCard';
import { AskQuestionButton } from '@/features/messages/AskQuestionButton';
import { ReportButton } from '@/features/reports/ReportButton';
import { useAuth } from '@/hooks/useAuth';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { ApiError } from '@/types/api';
import { ACCENT_VAR, accentStyle } from '@/utils/branding';
import { cn } from '@/utils/cn';
import {
  daysUntil,
  deadlineLabel,
  durationLabel,
  formatDate,
  formatDayMonth,
  humanizeWorkMode,
  placeLabel,
  stipendLabel,
  textToItems,
  timeAgo,
} from '@/utils/format';

/** The listing's own card style: 16px corners, a soft shadow, roomy padding. */
const PANEL =
  'border-line bg-surface min-w-0 rounded-[16px] border p-5 shadow-[0_1px_2px_rgb(30_20_60/0.05),0_6px_20px_-8px_rgb(30_20_60/0.12)] sm:p-6';

/** Past this, the exact number says more about us than about the role. */
const APPLICANTS_CAP = 100;

export function InternshipDetailPage() {
  const { identifier = '' } = useParams();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const save = useSaveInternship();
  const [applying, setApplying] = useState(false);
  const isStudent = user?.role === 'STUDENT';

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['internships', 'detail', identifier],
    queryFn: () => internshipApi.getInternship(identifier),
  });

  // Scored by id, so it waits for the detail call that resolves a slug.
  const { data: fit } = useQuery({
    queryKey: ['internship-match', data?.id],
    queryFn: () => matchingApi.getInternshipMatch(data.id),
    enabled: isStudent && Boolean(data?.id),
  });

  if (isPending) return <LoadingState label="Loading internship…" />;

  if (isError) {
    if (error instanceof ApiError && error.status === 404) {
      return (
        <Unavailable
          icon="internship"
          title="This internship is not available"
          message="It may have been filled, closed or removed. There are plenty of other roles open right now."
        />
      );
    }
    return (
      <div className="app-container py-12">
        <ErrorState
          title="Could not load this internship"
          message={errorMessage(error)}
          onRetry={() => void refetch()}
        />
        <div className="mt-6 text-center">
          <Link to={paths.internships} className="text-brand-600 text-sm font-medium">
            Back to all internships
          </Link>
        </div>
      </div>
    );
  }

  const days = daysUntil(data.application_deadline);
  const urgent = !data.is_open || (days !== null && days < 3);
  const applyBy = formatDayMonth(data.application_deadline);
  const applicants =
    data.applications_count > APPLICANTS_CAP ? `${APPLICANTS_CAP}+` : data.applications_count;

  /** "Posted: 1 week ago | Openings: 2 | Applicants: 3 | Apply by 15 Oct". */
  const footerFacts = [
    data.published_at && { label: 'Posted', value: timeAgo(data.published_at) },
    { label: 'Openings', value: data.positions },
    data.applications_count != null && { label: 'Applicants', value: applicants },
  ].filter(Boolean);

  return (
    <div className="app-container grid gap-6 py-8 lg:grid-cols-[minmax(0,1fr)_320px]">
      <div className="min-w-0 space-y-4">
        <Link
          to={paths.internships}
          className="text-ink-muted inline-flex items-center gap-1 text-sm hover:underline"
        >
          ← All internships
        </Link>

        {/* -- Header ------------------------------------------------------ */}
        <section className={PANEL} style={accentStyle(data.company.accent_color)}>
          <div className="flex items-start gap-4">
            <div className="min-w-0 flex-1">
              <h1 className="text-ink text-[22px] leading-tight font-bold tracking-tight break-words sm:text-2xl">
                {data.title}
              </h1>
              <p className="text-ink-muted mt-1.5 flex min-w-0 items-center gap-1.5 text-sm font-medium">
                {data.company.slug ? (
                  <Link
                    to={paths.companyPublic(data.company.slug)}
                    className="hover:text-brand-700 truncate hover:underline"
                  >
                    {data.company.name}
                  </Link>
                ) : (
                  <span className="truncate">{data.company.name}</span>
                )}
                {data.company.is_verified && (
                  <span
                    title="Verified company"
                    className="bg-success-500 grid size-4 shrink-0 place-items-center rounded-full text-white"
                  >
                    <Icon name="check" size={11} />
                    <span className="sr-only">Verified company</span>
                  </span>
                )}
              </p>
            </div>
            <CompanyLogo company={data.company} className="size-16" />
          </div>

          <div className="text-ink-muted mt-4 space-y-2 text-sm">
            <p className="flex flex-wrap items-center gap-y-1">
              {durationLabel(data.duration_months) && (
                <>
                  <Fact icon="clockOutline">{durationLabel(data.duration_months)}</Fact>
                  <span aria-hidden="true" className="bg-line mx-3 h-4 w-px" />
                </>
              )}
              <Fact icon="banknote">{stipendLabel(data)}</Fact>
            </p>
            {placeLabel(data) && (
              <p className="flex min-w-0 items-center">
                <Fact icon="mapPin" truncate>
                  {placeLabel(data)}
                </Fact>
              </p>
            )}
          </div>

          {!data.is_open && (
            <Alert tone="warning" className="mt-4">
              Applications for this internship are closed.
            </Alert>
          )}

          <div className="border-line mt-5 flex flex-wrap items-center justify-between gap-x-4 gap-y-3 border-t pt-4">
            <p className="text-ink-muted flex flex-wrap items-center gap-y-1 text-xs">
              {footerFacts.map((fact, index) => (
                <span key={fact.label} className="inline-flex items-center">
                  {index > 0 && <span aria-hidden="true" className="bg-line mx-2.5 h-3 w-px" />}
                  {fact.label}:&nbsp;<span className="text-ink font-medium">{fact.value}</span>
                </span>
              ))}
              {applyBy && (
                <span className="inline-flex items-center">
                  <span aria-hidden="true" className="bg-line mx-2.5 h-3 w-px" />
                  <span className={cn(urgent && 'text-danger-600 font-medium')}>
                    {data.is_open ? `Apply by ${applyBy}` : `Closed ${applyBy}`}
                  </span>
                </span>
              )}
            </p>

            <div className="flex flex-wrap items-center gap-2">
              {save.canSave && (
                <Button
                  variant={data.is_saved ? 'tintBrand' : 'secondary'}
                  size="sm"
                  isLoading={save.isPending}
                  onClick={() => save.toggle(data)}
                  aria-pressed={Boolean(data.is_saved)}
                >
                  {!save.isPending && <Icon name="bookmark" size={15} filled={data.is_saved} />}
                  {data.is_saved ? 'Saved' : 'Save'}
                </Button>
              )}
              <HeaderApply
                internship={data}
                isStudent={isStudent}
                isAuthenticated={isAuthenticated}
                onApply={() => setApplying(true)}
                onSignIn={() => navigate(paths.login, { state: { from: location } })}
              />
            </div>
          </div>
        </section>

        {/* -- Description: one flowing document ---------------------------- */}
        <section className={PANEL} aria-labelledby="description-heading">
          <h2 id="description-heading" className="text-ink text-lg font-semibold">
            Internship description
          </h2>

          <dl className="mt-4 space-y-1 text-sm">
            {placeLabel(data) && <LabelLine label="Location" value={placeLabel(data)} />}
            {data.application_deadline && (
              <LabelLine
                label="Apply by"
                value={`${formatDate(data.application_deadline)} (${deadlineLabel(data.application_deadline).toLowerCase()})`}
              />
            )}
          </dl>

          <div className="mt-4">
            <ListingProse text={data.description} />
          </div>

          <DescriptionSection title="Responsibilities" text={data.responsibilities} />
          <DescriptionSection title="Requirements / who can apply" text={data.requirements} />
          <DescriptionSection title="Perks" text={data.perks} />

          <div className="border-line mt-6 border-t pt-5">
            <h3 className="text-ink text-[15px] font-semibold">Role details</h3>
            <dl className="mt-3 grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
              <DetailLine label="Category" value={data.job_category} />
              <DetailLine label="Industry" value={data.industry} />
              <DetailLine label="Work mode" value={humanizeWorkMode(data.work_mode)} />
              <DetailLine label="Duration" value={durationLabel(data.duration_months)} />
              <DetailLine
                label="Start date"
                value={data.starts_on ? formatDate(data.starts_on) : null}
              />
              <DetailLine label="Openings" value={String(data.positions)} />
              <DetailLine label="Stipend" value={stipendLabel(data)} />
              <DetailLine
                label="Posted"
                value={data.published_at ? formatDate(data.published_at) : null}
              />
            </dl>
          </div>

          <KeySkills skills={data.required_skills} />
        </section>
      </div>

      <aside className="min-w-0 space-y-4 lg:pt-9">
        {isStudent && fit && (
          <Card>
            <CardHeader
              title="How you fit"
              description="Only you see this."
              action={<MatchBadge match={fit} />}
            />
            <MatchBreakdown match={fit} />
          </Card>
        )}

        {isStudent && data.id && <ResumeMatchCard internshipId={data.id} />}

        <CompanyCard company={data.company} />

        <Card>
          <div className="space-y-2">
            {/* A private question to the company, before or instead of
                applying. One thread per listing; once asked, this opens it. */}
            {isStudent && <AskQuestionButton internship={data} />}
            <ShareButton
              title={data.title}
              text={`${data.title} at ${data.company.name} on Join-Wonder`}
            />
            {/* Last and quietest. A prominent report button on every listing
                gets used as a downvote, and the queue behind this one is read
                by a person. */}
            <ReportButton targetType="INTERNSHIP" targetId={data.id} targetName={data.title} />
          </div>
        </Card>
      </aside>

      {isStudent && (
        <ApplyDialog internship={data} open={applying} onClose={() => setApplying(false)} />
      )}
    </div>
  );
}

/** The header's main action, by who is looking and where the listing stands. */
function HeaderApply({ internship, isStudent, isAuthenticated, onApply, onSignIn }) {
  if (!isAuthenticated) {
    return internship.is_open ? (
      <Button size="sm" onClick={onSignIn} title="Sign in to apply">
        <Icon name="send" size={14} />
        Apply now
      </Button>
    ) : null;
  }
  // Companies and admins cannot apply.
  if (!isStudent) return null;
  if (internship.has_applied) {
    return (
      <Badge tone="success" className="px-2.5 py-1">
        <Icon name="check" size={13} />
        Applied
      </Badge>
    );
  }
  if (!internship.is_open) {
    return (
      <Badge className="px-2.5 py-1" tone="neutral">
        Closed
      </Badge>
    );
  }
  return (
    <Button size="sm" onClick={onApply}>
      <Icon name="send" size={14} />
      Apply now
    </Button>
  );
}

function Fact({ icon, children, truncate = false }) {
  return (
    <span className={cn('inline-flex items-center gap-1.5', truncate && 'min-w-0')}>
      <Icon name={icon} size={16} className="text-ink-muted/70" />
      <span className={cn(truncate && 'truncate')}>{children}</span>
    </span>
  );
}

/** "Location – Kolkata": the short lines at the top of the description. */
function LabelLine({ label, value }) {
  return (
    <div className="flex flex-wrap gap-x-1.5">
      <dt className="text-ink font-medium">{label} –</dt>
      <dd className="text-ink/85 min-w-0 break-words">{value}</dd>
    </div>
  );
}

/** A bold sub-heading and its bullets; nothing at all when the text is empty. */
function DescriptionSection({ title, text }) {
  if (textToItems(text).length === 0) return null;
  return (
    <section className="mt-6">
      <h3 className="text-ink mb-2 text-[15px] font-semibold">{title}</h3>
      <ListingItems text={text} />
    </section>
  );
}

/** "Category: Software Engineering"; skipped when there is no value. */
function DetailLine({ label, value }) {
  if (value == null || value === '') return null;
  return (
    <div className="flex min-w-0 gap-1.5">
      <dt className="text-ink-muted shrink-0">{label}:</dt>
      <dd className="text-ink min-w-0 font-medium break-words">{value}</dd>
    </div>
  );
}

/**
 * Outline chips. Required skills carry a star, as a job board marks the ones
 * that matter; the legend only appears when some are required and some not.
 */
function KeySkills({ skills }) {
  if (!skills?.length) return null;
  const required = skills.filter((entry) => entry.is_required).length;
  const mixed = required > 0 && required < skills.length;
  return (
    <div className="border-line mt-6 border-t pt-5">
      <h3 className="text-ink text-[15px] font-semibold">Key skills</h3>
      {(mixed || required > 0) && (
        <p className="text-ink-muted mt-1 text-xs">
          {mixed
            ? 'Skills marked with ★ are required; the rest are nice to have.'
            : 'All skills marked ★ are required.'}
        </p>
      )}
      <ul className="mt-3 flex flex-wrap gap-2" aria-label="Key skills">
        {skills.map((entry) => (
          <li
            key={entry.skill.id}
            className="border-line text-ink inline-flex items-center gap-1 rounded-full border px-3 py-1 text-[13px]"
          >
            {entry.is_required && <Icon name="star" size={12} className="text-warning-500" />}
            {entry.skill.name}
            {entry.is_required && <span className="sr-only"> (required)</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The company behind the listing, in its own accent, linking to its page. */
function CompanyCard({ company }) {
  return (
    <div style={accentStyle(company.accent_color)}>
      <Card>
        <p className="text-ink-muted mb-3 text-xs font-medium tracking-wide uppercase">
          About the company
        </p>
        <div className="flex items-start gap-3">
          <CompanyLogo company={company} className="size-11" iconSize={18} />
          <div className="min-w-0">
            <p className="text-ink font-medium">{company.name}</p>
            {company.tagline && (
              <p className="text-ink-muted mt-0.5 truncate text-sm" title={company.tagline}>
                {company.tagline}
              </p>
            )}
          </div>
        </div>
        {(company.industry || company.location) && (
          <p className="text-ink-muted mt-3 text-sm">
            {[company.industry, company.location].filter(Boolean).join(' · ')}
          </p>
        )}
        {company.slug && (
          <Link
            to={paths.companyPublic(company.slug)}
            className="mt-3 inline-flex items-center gap-1 text-sm font-medium hover:underline"
            style={{ color: ACCENT_VAR }}
          >
            About {company.name}
            <Icon name="arrowRight" size={14} />
          </Link>
        )}
      </Card>
    </div>
  );
}
